import { useEffect, useRef } from 'react';
import { APP_VERSION } from '@/config/version';
import { detectMobileBrowser } from '@/utils/versionTracker';

const VERSION_STORAGE_KEY = 'luvall-build-id';
const CHECK_INTERVAL = 2 * 60 * 1000; // Check every 2 minutes

/**
 * Hook that proactively checks for version changes and auto-reloads
 * when a new version is detected. This ensures users always get
 * the latest version without manual cache clearing.
 * 
 * Uses BUILD_TIMESTAMP which is injected at build time by Vite,
 * ensuring each publish creates a unique identifier.
 */
export const useVersionCheck = () => {
  const lastCheck = useRef<number>(0);
  const checkInterval = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const { isIOS } = detectMobileBrowser();
    
    // Delay initialization on iOS to prevent crashes
    const initDelay = isIOS ? 3000 : 500;

    const checkVersion = async () => {
      try {
        const now = Date.now();
        // Throttle checks
        if (now - lastCheck.current < 30000) return;
        lastCheck.current = now;

        const storedBuildId = localStorage.getItem(VERSION_STORAGE_KEY);
        // Use buildId which includes the build timestamp (changes on every publish)
        const currentBuildId = APP_VERSION.buildId;

        if (!storedBuildId) {
          localStorage.setItem(VERSION_STORAGE_KEY, currentBuildId);
          console.log('📦 Version check: First visit, stored build ID', currentBuildId);
          return;
        }

        if (storedBuildId !== currentBuildId) {
          console.log('📦 Build mismatch detected - new version available!', {
            stored: storedBuildId,
            current: currentBuildId
          });

          // Update stored build ID
          localStorage.setItem(VERSION_STORAGE_KEY, currentBuildId);

          // Clear all caches
          if ('caches' in window) {
            const cacheNames = await caches.keys();
            await Promise.all(cacheNames.map(name => caches.delete(name)));
            console.log('🗑️ Caches cleared for version update');
          }

          // Trigger service worker update
          if ('serviceWorker' in navigator) {
            const registration = await navigator.serviceWorker.getRegistration();
            if (registration) {
              await registration.update();
              if (registration.waiting) {
                registration.waiting.postMessage({ type: 'SKIP_WAITING' });
              }
            }
          }

          // Reload to get fresh version
          console.log('🔄 Reloading for new version...');
          window.location.reload();
        }
      } catch (error) {
        console.warn('Version check failed:', error);
      }
    };

    // Initial check after delay
    const timeoutId = setTimeout(() => {
      checkVersion();
      
      // Set up periodic checks
      checkInterval.current = setInterval(checkVersion, CHECK_INTERVAL);
    }, initDelay);

    // Also check on visibility change
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkVersion();
      }
    };

    // Check on focus
    const handleFocus = () => {
      checkVersion();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      clearTimeout(timeoutId);
      if (checkInterval.current) {
        clearInterval(checkInterval.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);
};

export default useVersionCheck;
